/**
 * Custom hook for using Pusher in React components
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import Pusher from 'pusher-js';
import logger from '../utils/logger';

// Pusher configuration
const PUSHER_KEY = process.env.REACT_APP_PUSHER_KEY;
const PUSHER_CLUSTER = process.env.REACT_APP_PUSHER_CLUSTER || 'us2';

// Create a singleton Pusher client instance
let pusherClient = null;
// Connection state tracked for potential future use (e.g. UI indicator)
// eslint-disable-next-line no-unused-vars
let _pusherClientConnected = false;
let connectionAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 5;

// Add global connection listeners to the Pusher client
const initGlobalConnectionHandlers = (pusher) => {
  if (!pusher) return;
  
  // Enhanced connection state monitoring
  pusher.connection.bind('state_change', (states) => {
    logger.info('🔌 PUSHER: Connection state changed:', {
      previous: states.previous,
      current: states.current,
      timestamp: new Date().toISOString()
    });
    
    if (states.current === 'connected') {
      logger.info('🔌 PUSHER: Successfully connected to Pusher');
      _pusherClientConnected = true;
      connectionAttempts = 0;
    } else if (states.current === 'disconnected') {
      logger.info('🔌 PUSHER: Disconnected from Pusher');
      _pusherClientConnected = false;
    } else if (states.current === 'connecting') {
      logger.info('🔌 PUSHER: Connecting to Pusher...');
    } else if (states.current === 'failed') {
      logger.error('🔌 PUSHER: Connection failed');
      _pusherClientConnected = false;
    }
  });
  
  // Connection succeeded
  pusher.connection.bind('connected', () => {
    logger.info('🔌 PUSHER: Connection established!');
    _pusherClientConnected = true;
    connectionAttempts = 0;
  });
  
  // Connection failed
  pusher.connection.bind('failed', () => {
    logger.error('🔌 PUSHER: Connection failed');
    _pusherClientConnected = false;
  });
  
  // Connection disconnected
  pusher.connection.bind('disconnected', () => {
    logger.info('🔌 PUSHER: Disconnected');
    _pusherClientConnected = false;
    
    // Attempt to reconnect if not at max attempts
    if (connectionAttempts < MAX_RECONNECT_ATTEMPTS) {
      connectionAttempts++;
      logger.info(`🔌 PUSHER: Attempting to reconnect (${connectionAttempts}/${MAX_RECONNECT_ATTEMPTS})...`);
      pusher.connect();
    } else {
      logger.error('🔌 PUSHER: Max reconnection attempts reached');
    }
  });
  
  // Error handling
  pusher.connection.bind('error', (err) => {
    logger.error('🔌 PUSHER: Connection error:', err);
    if (err.error && err.error.data) {
      logger.error('🔌 PUSHER: Error details:', err.error.data);
    }
  });
};

const getPusherClient = () => {
  if (!pusherClient) {
    if (!PUSHER_KEY) {
      logger.error('Pusher key is not defined in environment variables');
      return null;
    }
    
    // Enable detailed logging in development
    if (process.env.NODE_ENV === 'development') {
      Pusher.logToConsole = true;
    }
    
    pusherClient = new Pusher(PUSHER_KEY, {
      cluster: PUSHER_CLUSTER,
      forceTLS: true,
      enabledTransports: ['ws', 'wss'], // Use WebSockets only for more reliability
    });
    
    // Add global connection handlers
    initGlobalConnectionHandlers(pusherClient);
    
    logger.info('Created Pusher client with config:', {
      key: PUSHER_KEY?.substring(0, 5) + '...',
      cluster: PUSHER_CLUSTER
    });
  }
  
  return pusherClient;
};

/**
 * Custom hook to monitor Pusher connection state
 * @returns {boolean} Whether Pusher is connected
 */
export const usePusherConnection = () => {
  const [isConnected, setIsConnected] = useState(false);
  
  useEffect(() => {
    const pusher = getPusherClient();
    if (!pusher) return;
    
    // Set initial state
    setIsConnected(pusher.connection.state === 'connected');
    
    // Add listener for connection changes
    const handleConnected = () => setIsConnected(true);
    const handleDisconnected = () => setIsConnected(false);
    
    pusher.connection.bind('connected', handleConnected);
    pusher.connection.bind('disconnected', handleDisconnected);
    
    return () => {
      pusher.connection.unbind('connected', handleConnected);
      pusher.connection.unbind('disconnected', handleDisconnected);
    };
  }, []);
  
  return isConnected;
};

/**
 * Custom hook for subscribing to Pusher channels
 * @param {string} channelName - The name of the channel to subscribe to
 * @returns {Object} The Pusher channel object
 */
export const usePusherChannel = (channelName) => {
  const [channel, setChannel] = useState(null);
  const channelRef = useRef(null);
  
  useEffect(() => {
    // Don't subscribe if the channel name is empty or invalid
    if (!channelName || channelName === '') {
      setChannel(null);
      channelRef.current = null;
      return;
    }
    
    const pusher = getPusherClient();
    if (!pusher) return;
    
    logger.info(`Subscribing to Pusher channel: ${channelName}`);
    
    // Check if we're already subscribed to avoid duplicate subscriptions
    const existingChannel = pusher.channel(channelName);
    if (existingChannel) {
      logger.info(`Using existing Pusher channel: ${channelName}`);
      setChannel(existingChannel);
      channelRef.current = existingChannel;
      return;
    }
    
    // Subscribe to the channel
    const pusherChannel = pusher.subscribe(channelName);
    
    // Add subscription succeeded handler
    pusherChannel.bind('pusher:subscription_succeeded', () => {
      logger.info(`🎧 KID B CONNECTED: I'm now listening to channel ${channelName} for messages!`);
      
      // Log all events on this channel for debugging
      pusherChannel.bind_global((event, data) => {
        logger.info(`🎧 KID B DEBUG: Received ${event} event on channel ${channelName}`, data);
      });
    });
    
    // Add subscription error handler
    pusherChannel.bind('pusher:subscription_error', (error) => {
      logger.error(`❌ KID B ERROR: Failed to subscribe to channel ${channelName}:`, error);
    });
    
    // Store the channel in state and ref
    setChannel(pusherChannel);
    channelRef.current = pusherChannel;
    
    // Cleanup: unsubscribe when the component unmounts or the channel name changes
    return () => {
      if (pusher && pusher.channel(channelName)) {
        logger.info(`Unsubscribing from Pusher channel: ${channelName}`);
        pusher.unsubscribe(channelName);
      }
      channelRef.current = null;
    };
  }, [channelName]);
  
  return channel;
};

/**
 * Custom hook for binding to Pusher events with improved reliability
 * @param {Object} channel - The Pusher channel object
 * @param {string} eventName - The name of the event to bind to
 * @param {Function} callback - The callback function to execute when the event is received
 */
export const usePusherEvent = (channel, eventName, callback) => {
  // Make sure callback reference is stable
  const callbackRef = useRef(callback);
  const boundRef = useRef(false);
  
  // Update the ref when callback changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);
  
  // Create a stable wrapper function
  const stableCallback = useCallback((data) => {
    logger.info(`📣 KID B ALERT: Tower is sending ${eventName} event to my walkie-talkie!`, {
      event: eventName,
      data: typeof data === 'object' ? {
        ...data,
        content: data.content ? data.content.substring(0, 30) + (data.content?.length > 30 ? '...' : '') : undefined
      } : data
    });
    callbackRef.current(data);
  }, [eventName]);
  
  useEffect(() => {
    // Check if channel is valid and has bind method
    if (!channel || !eventName || !callback || typeof channel.bind !== 'function') return;
    
    logger.info(`Binding to Pusher event: ${eventName}`);
    
    // Only unbind the specific callback to prevent duplicates without interfering with first events
    // Don't unbind all callbacks for this event - just ensure we don't double-bind
    if (boundRef.current) {
      channel.unbind(eventName, stableCallback);
    }
    
    // Bind new callback
    channel.bind(eventName, stableCallback);
    boundRef.current = true;
    
    // Cleanup: unbind when the component unmounts or dependencies change
    return () => {
      // Safety check to make sure channel still has unbind method
      if (channel && typeof channel.unbind === 'function' && boundRef.current) {
        logger.info(`Unbinding from Pusher event: ${eventName}`);
        channel.unbind(eventName, stableCallback);
        boundRef.current = false;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- callback in ref, omit to avoid re-bind on every callback change
  }, [channel, eventName, stableCallback]);
};

/**
 * Custom hook to subscribe to a user's private channel for notifications
 * @param {string} userId - The user ID to subscribe to
 * @returns {object} Channel object and connection status
 */
export const usePusherUserChannel = (userId) => {
  const [userChannel, setUserChannel] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  
  useEffect(() => {
    if (!userId) return;
    
    const pusher = getPusherClient();
    if (!pusher) return;
    
    const channelName = `user-${userId}`;
    const channel = pusher.subscribe(channelName);
    
    setUserChannel(channel);
    
    // Monitor channel subscription state
    channel.bind('pusher:subscription_succeeded', () => {
      logger.info(`🔔 PUSHER: Subscribed to user channel: ${channelName}`);
      setIsConnected(true);
    });
    
    channel.bind('pusher:subscription_error', (error) => {
      logger.error(`🔔 PUSHER: Failed to subscribe to user channel: ${channelName}`, error);
      setIsConnected(false);
    });
    
    // Cleanup on unmount
    return () => {
      if (channel) {
        pusher.unsubscribe(channelName);
        setUserChannel(null);
        setIsConnected(false);
      }
    };
  }, [userId]);
  
  return { userChannel, isConnected };
};

const usePusherExports = { usePusherConnection, usePusherChannel, usePusherEvent, usePusherUserChannel };
export default usePusherExports; 